<template>
  <div class="container" v-if="!formSendError">
    <div class="section__block section__block--centered">
      <h1 class="headline headline--h1">{{ t('et.title') }}</h1>
    </div>
    <div class="section__block">
      <form class="form form--initialized form--desktop-horizontal" @submit.prevent="handleSubmit">
        <div class="form__body">
          <div class="form__group">
            <div role="group" class="form__control form__control--desktop-horizontal" :aria-labelledby="'step1-' + uniqueId">
              <div class="form__control-header">
                <div class="form__control-icon">
                  <span class="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" :aria-labelledby="'step1-title-' + uniqueId"><title :id="'step1-title-' + uniqueId">{{ t('et.steps.1.step') }}</title>
                      <path d="M21.137 21.112v2.033h-8.278v-2.033h3.104v-7.76l-2.711.73v-1.997l4.834-1.445v10.472h3.05Z"></path>
                      <path fill-rule="evenodd" d="M34 17c0 9.389-7.611 17-17 17S0 26.389 0 17 7.611 0 17 0s17 7.611 17 17Zm-1.784 0c0 8.404-6.812 15.216-15.216 15.216S1.784 25.404 1.784 17 8.596 1.784 17 1.784 32.216 8.596 32.216 17Z" clip-rule="evenodd"></path>
                    </svg>
                  </span>
                </div>
                <div class="form__control-text">
                  <div class="form__control-headlines" :id="'step1-' + uniqueId">
                    <span class="form__control-headline headline headline--accent headline--accent-sm ">{{ t('et.steps.1.subtitle') }}</span>
                    <span class="form__control-headline headline headline--h4 ">{{ t('et.steps.1.title') }}</span>
                  </div>
                </div>
              </div>
              <div class="form__control-content">
                <div class="form__control-content-inner">
                  <div class="form__row">
                    <div class="row">
                      <LocationFinderSelect v-model="data.locationfinder"
                                            @validation-change="handleStudioValidation"
                                            :force-validation="forceValidationOnSubmit"
                                            :inverted="true"
                                            id="et-studio"
                                            :disableBiaStudios="data.bia"
                                            :rules="studioRules"></LocationFinderSelect>
                    </div>
                  </div>
                  <div class="form__row" v-if="data.locationfinder && selectedStudio">
                    <div class="row">
                      <h4 class="headline headline--h5">Kieser Studio</h4>
                      <div class="form__divider form__divider--desktop-display-none"></div>
                      <p>
                        <strong>{{ selectedStudio.name }}</strong><br />{{ selectedStudio.street }}<br />{{ selectedStudio.plz }} {{ selectedStudio.city }}
                      </p>
                      <span style="display: inline-block; width: auto;" v-if="data.bia && !selectedStudio.bia" class="badge badge--accent">{{ t('locationfinder.nobia') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="form__group">
            <fieldset class="form__control form__control--desktop-horizontal" :aria-labelledby="'step2-' + uniqueId">
              <div class="form__control-header">
                <div class="form__control-icon">
                  <span class="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" :aria-labelledby="'step2-title-' + uniqueId"><title :id="'step2-title-' + uniqueId">{{ t('et.steps.2.step') }}</title>
                      <path fill-rule="evenodd" d="M13.136 20.782c-.06.185-.112.378-.156.58a8.442 8.442 0 0 0-.175 1.783h8.367v-2.033h-5.887a2.87 2.87 0 0 1 .49-1.044 4.563 4.563 0 0 1 .884-.91c.35-.28.743-.544 1.177-.794.607-.35 1.154-.71 1.642-1.079a4.302 4.302 0 0 0 1.16-1.302c.285-.506.427-1.139.427-1.9 0-.69-.157-1.308-.473-1.856a3.259 3.259 0 0 0-1.355-1.302c-.589-.32-1.3-.482-2.132-.482-.642 0-1.24.114-1.793.34a4.348 4.348 0 0 0-1.427.945 4.034 4.034 0 0 0-.919 1.41l1.891.856c.035-.088.073-.172.115-.253a2.49 2.49 0 0 1 .732-.88c.381-.28.86-.42 1.437-.42.547 0 .98.155 1.302.464.321.304.481.714.481 1.231 0 .375-.095.708-.285 1a3.097 3.097 0 0 1-.731.775 9.07 9.07 0 0 1-.982.652c-.547.315-1.07.669-1.57 1.061a7.37 7.37 0 0 0-1.32 1.365 6.115 6.115 0 0 0-.9 1.793Z" clip-rule="evenodd"></path>
                      <path fill-rule="evenodd" d="M17 32.216c8.404 0 15.216-6.812 15.216-15.216S25.404 1.784 17 1.784 1.784 8.596 1.784 17 8.596 32.216 17 32.216ZM17 34c9.389 0 17-7.611 17-17S26.389 0 17 0 0 7.611 0 17s7.611 17 17 17Z" clip-rule="evenodd"></path>
                    </svg>
                  </span>
                </div>
                <div class="form__control-text">
                  <div class="form__control-headlines" :id="'step2-' + uniqueId">
                    <span class="form__control-headline headline headline--accent headline--accent-sm">{{ t('et.steps.2.subtitle') }}</span>
                    <span class="form__control-headline headline headline--h4">{{ t('et.steps.2.title') }}</span>
                  </div>
                </div>
              </div>
              <div class="form__control-content">
                <div class="form__control-content-inner">
                  <div class="form__row">
                    <div class="row">
                      <InterestCheckbox id="et-action"
                                        v-model="data.action"
                                        :inverted="true"
                                        :has-error="!isValidInterest"
                                        :error-id="'et-error-interest' + uniqueId"
                                        :helptext="actionHelptext"
                                        :label="t('et.action.label')" />
                    </div>
                    <div class="row">
                      <InterestCheckbox id="et-bia"
                                        v-model="data.bia"
                                        :inverted="true"
                                        :has-error="!isValidInterest"
                                        :error-id="'et-error-interest' + uniqueId"
                                        :dialog="{
                                          headline: t('et.bia.dialog.headline'),
                                          copy: t('et.bia.dialog.copy'),
                                        }"
                                        :helptext="biaHelptext"
                                        :label="t('et.bia.label')" />
                    </div>
                    <div class="row row--with-border" v-if="!isValidInterest">
                      <Error :inverted="true"
                             :id="'et-error-interest' + uniqueId"
                             :message="t('et.interest.error.empty')" />
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
          <div class="form__group">
            <div class="form__row">
              <fieldset class="form__control form__control--desktop-horizontal" :aria-labelledby="'step3-' + uniqueId" :aria-describedby="'step4-' + uniqueId">
                <div class="form__control-header">
                  <div class="form__control-icon">
                    <span class="icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" fill="currentColor" viewBox="0 0 35 35" :aria-labelledby="'step3-title-' + uniqueId"><title :id="'step3-title-' + uniqueId">Dritte</title>
                        <path d="M15.322 23.235c.559.244 1.198.366 1.918.366.832 0 1.57-.151 2.212-.455a3.573 3.573 0 0 0 1.516-1.32c.369-.577.553-1.263.553-2.06 0-.744-.19-1.374-.57-1.891a3.158 3.158 0 0 0-1.428-1.106c.476-.28.85-.651 1.124-1.115.274-.464.41-.99.41-1.58 0-.647-.163-1.227-.49-1.739a3.328 3.328 0 0 0-1.338-1.204c-.571-.297-1.228-.446-1.971-.446-.637 0-1.208.113-1.713.34a4.042 4.042 0 0 0-1.311.927 4.66 4.66 0 0 0-.866 1.338l1.91.731c.124-.238.276-.458.454-.66.179-.202.393-.366.643-.49.25-.125.544-.188.883-.188.51 0 .918.149 1.222.446.303.298.454.684.454 1.16 0 .488-.16.877-.481 1.168-.315.292-.738.438-1.267.438h-1.07v2.015h1.124c.731 0 1.27.179 1.614.536.351.356.527.802.527 1.338 0 .392-.093.725-.277.999a1.78 1.78 0 0 1-.75.615 2.642 2.642 0 0 1-1.06.205 2.46 2.46 0 0 1-1.375-.401c-.404-.268-.749-.705-1.034-1.311l-1.909.785c.226.582.535 1.094.928 1.534.392.44.865.782 1.418 1.025Z"></path>
                        <path fill-rule="evenodd" d="M34.242 17.242c0 9.389-7.611 17-17 17s-17-7.611-17-17c0-9.39 7.611-17 17-17s17 7.61 17 17Zm-1.784 0c0 8.403-6.813 15.216-15.216 15.216-8.404 0-15.216-6.813-15.216-15.216 0-8.404 6.812-15.216 15.216-15.216 8.403 0 15.216 6.812 15.216 15.216Z" clip-rule="evenodd"></path>
                      </svg>
                    </span>
                  </div>
                  <div class="form__control-text">
                    <div class="form__control-headlines" :id="'step3-' + uniqueId">
                      <span class="form__control-headline  headline headline--h4 ">{{ t('et.steps.3.title') }}</span>
                    </div>
                    <div class="form__control-description" id="nS67Cs">{{ t('et.steps.3.copy') }}</div>
                  </div>
                </div>

                <!-- Input Element -->
                <div class="form__control-content">
                  <div class="form__control-content-inner">
                    <div class="form__row">
                      <div class="row">
                        <div class="col-12 col-xl-6">
                          <SelectField :label="t('et.salutation.label')"
                                       id="et-salutation"
                                       v-model="data.salutation"
                                       :options="tm('et.salutation.options')"
                                       @validation-change="handleSalutationValidation"
                                       :force-validation="forceValidationOnSubmit"
                                       :rules="salutationRules"
                                       :inverted="true" />
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12 col-xl-6">
                          <InputField :label="t('et.firstname.label')"
                                      id="et-firstname"
                                      v-model="data.firstname"
                                      :inverted="true"
                                      @validation-change="handleFirstNameValidation"
                                      :force-validation="forceValidationOnSubmit"
                                      :rules="firstnameRules"
                                      :placeholder="t('et.firstname.placeholder')"
                                      autocomplete="given-name" />
                        </div>
                        <div class="col-12 col-xl-6">
                          <InputField :label="t('et.lastname.label')"
                                      id="et-lastname"
                                      v-model="data.lastname"
                                      :inverted="true"
                                      @validation-change="handleLastNameValidation"
                                      :force-validation="forceValidationOnSubmit"
                                      :rules="lastnameRules"
                                      :placeholder="t('et.lastname.placeholder')"
                                      autocomplete="family-name" />
                        </div>
                      </div>
                      <div class="row">
                        <InputField :label="t('et.email.label')"
                                    id="et-email"
                                    v-model="data.email"
                                    :inverted="true"
                                    type="email"
                                    @validation-change="handleEmailValidation"
                                    :force-validation="forceValidationOnSubmit"
                                    :rules="emailRules"
                                    :placeholder="t('et.email.placeholder')"
                                    autocomplete="email" />
                      </div>
                      <div class="row">
                        <PhoneSelectInput :label="t('et.phone.label')"
                                          id="et-phone"
                                          v-model="data.phone"
                                          :inverted="true"
                                          :cc="countryCodes"
                                          @validation-change="handlePhoneValidation"
                                          :force-validation="forceValidationOnSubmit"
                                          :rules="phoneRules"
                                          :placeholder="t('et.phone.placeholder')"
                                          autocomplete="tel" />
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div class="form__group form__group--tablet-rounded">
            <fieldset class="form__control form__control--desktop-horizontal" :aria-labelledby="'step4-' + uniqueId" aria-describedby="Q5aCTG">
              <div class="form__control-header">
                <div class="form__control-icon">
                <span class="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-labelledby="title-eqcua9yf1"><title id="title-eqcua9yf1">vierte</title>
                    <path d="M18.193 23.122V20.41h-5.976v-1.32l5.976-8.456h2.07v7.832h1.587v1.944h-1.588v2.712h-2.069Zm-3.229-4.656h3.23v-4.87l-3.23 4.87Z"></path>
                    <path fill-rule="evenodd" d="M12.217 19.09v1.32h5.976v2.712h2.07V20.41h1.587v-1.944h-1.588v-7.832h-2.069l-5.976 8.456Zm5.976-5.495-3.229 4.87h3.23v-4.87Z" clip-rule="evenodd"></path>
                    <path fill-rule="evenodd" d="M17 32.216c8.404 0 15.216-6.812 15.216-15.216S25.404 1.784 17 1.784 1.784 8.596 1.784 17 8.596 32.216 17 32.216ZM17 34c9.389 0 17-7.611 17-17S26.389 0 17 0 0 7.611 0 17s7.611 17 17 17Z" clip-rule="evenodd"></path>
                  </svg>
                </span>
                </div>
                <div class="form__control-text">
                  <div class="form__control-headlines" :id="'step4-' + uniqueId">
                    <span class="form__control-headline  headline headline--h4 ">{{ t('et.steps.4.title') }}</span>
                    <div class="form__control-overlay-trigger">
                    </div>
                  </div>
                  <div class="form__control-description" id="Q5aCTG">{{ t('et.steps.4.copy') }}</div>
                </div>
              </div>

              <!-- Input Element -->
              <div class="form__control-content form__control-content--desktop-pb-xxl">
                <div class="form__control-content-inner">
                  <div class="form__row">
                    <div class="row">
                      <DateRadioButtons id="et-date"
                                        :disabled="!data.locationfinder.length || (data.locationfinder.length && !isValidStudio)"
                                        :options="selectedStudioDates"
                                        name="et-date"
                                        v-model="data.day"
                                        @validation-change="handleDateValidation"
                                        :force-validation="forceValidationOnSubmit"
                                        :rules="dateRules"
                                        :label="t('et.date.label')" />
                      <div class="col-12 col-xl-5">
                        <SelectField :label="t('et.time.label')"
                                     id="et-time"
                                     v-model="data.time"
                                     :disabled="!selectedStudioDateTimes.length"
                                     :options="selectedStudioDateTimes"
                                     @validation-change="handleTimeValidation"
                                     :force-validation="forceValidationOnSubmit"
                                     :rules="timeRules"
                                     :large="false"
                                     :inverted="true" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
        <div class="form__footer-panel">
          <div class="form__row form__row--mb-lg">
            <div class="row">
              <div class="col-12">
                <Checkbox id="et-data"
                          v-model="data.data"
                          :inverted="true"
                          @validation-change="handleDataValidation"
                          :force-validation="forceValidationOnSubmit"
                          :rules="dataRules"
                          :helptext="`${t('et.data.helptextPreLink')}<a href='${countryLanguagePath}${t('et.data.link')}' class='link link--negative' target='_blank'>${t('et.data.helptextLink')}</a>${t('et.data.helptextPostLink')}`"
                          :label="t('et.data.label')"/>
                <Checkbox id="et-privacy"
                          v-model="data.privacy"
                          :inverted="true"
                          @validation-change="handlePrivacyValidation"
                          :force-validation="forceValidationOnSubmit"
                          :rules="privacyRules"
                          :helptext="t('et.privacy.helptext')"
                          :label="t('et.privacy.label')"/>
                <Checkbox id="et-newsletter"
                          v-model="data.newsletter"
                          :inverted="true"
                          :helptext="`${t('et.newsletter.helptextPreLink')}<a href='${countryLanguagePath}${t('et.newsletter.link')}' class='link link--negative' target='_blank'>${t('et.newsletter.helptextLink')}</a>${t('et.newsletter.helptextPostLink')}`"
                          :label="t('et.newsletter.label')"/>
              </div>
            </div>
          </div>
          <div class="form__row form__row--text-center">
            <Submit :loading="isSubmitLoading"
                    :label="t('et.submit.label')" />
          </div>
        </div>
      </form>
    </div>
  </div>
  <div class="container" aria-live="polite" v-if="!isFormSend && formSendError">
    <div class="simple-2col  simple-2col--with-navy-bg simple-2col--gap-lg">
      <div class="simple-2col__col simple-2col__col--with-navy-bg simple-2col__col--align-center simple-2col__col--no-padding-top simple-2col__col--no-padding-x">
        <div class="simple-2col__header">
          <h2 class="headline headline--h1">{{ t('et.error.title') }}</h2>
        </div>
        <div class="simple-2col__content">
          <p>{{ t('et.error.copy') }}</p>
        </div>
      </div>
      <div class="simple-2col__col simple-2col__col--with-blue-bg simple-2col__col--align-center">
        <span class="icon icon--lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34">
            <path d="M17 30C9.832 30 4 24.168 4 17S9.832 4 17 4s13 5.832 13 13-5.832 13-13 13Zm0-24.953C10.408 5.047 5.047 10.408 5.047 17S10.408 28.953 17 28.953 28.953 23.592 28.953 17 23.592 5.047 17 5.047Z"></path>
            <path fill-rule="evenodd" d="M11.428 14.2a1.019 1.019 0 0 0-.028.24v5.959c0 .571.461.993.978.993h9.226a.986.986 0 0 0 .979-.993v-5.991l-5.295 3.146a.585.585 0 0 1-.609-.01l-5.251-3.343Zm.832-.746 4.742 3.02 4.97-2.954a.962.962 0 0 0-.368-.073h-9.226c-.04 0-.08.002-.118.007Zm-1.978.986c0-1.111.915-2.053 2.096-2.053h9.226c1.181 0 2.097.941 2.097 2.053v5.959c0 1.111-.916 2.053-2.097 2.053h-9.226c-1.18 0-2.096-.942-2.096-2.053v-5.96Z" clip-rule="evenodd"></path>
          </svg>
        </span>
        <h4 class="headline headline--h4">{{ t('et.error.email') }}</h4>
        <p>{{ t('et.error.emailCopy') }} <a class="link link--negative" href="mailto:info@kieser.com">info@kieser.com</a>.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {reactive, ref, computed, useTemplateRef, watch, useId, onBeforeMount} from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import InputField from './components/fields/InputField.vue';
import Submit from './components/fields/Submit.vue';
import {
  checkCookies,
  getBrowser, getCookie,
  getCountryAndLanguagePathFromUrl,
  getCountryFromUrl,
  getLanguageFromUrl,
} from '../../abstracts/utilities.js';
import {getStudioUidByName, getStudioByName, normalizeEmailForUserHash, hashSha256, trackData} from './utilities.js';
import SelectField from './components/fields/SelectField.vue';
import LocationFinderSelect from './components/fields/LocationFinderSelect.vue';
import Checkbox from './components/fields/Checkbox.vue';
import DateRadioButtons from './components/et-fields/DateRadioButtons.vue';
import PhoneSelectInput from './components/et-fields/PhoneSelectInput.vue';
import Error from './components/fields/Error.vue';
import InterestCheckbox from './components/et-fields/InterestCheckbox.vue';
import {initialAdsCookie} from '../../abstracts/kies_ad_et.js';

const { t, tm } = useI18n();
const success = useTemplateRef('success');
const error = useTemplateRef('error');
const uniqueId = useId();

const countryLanguagePath = window.location.origin + '/' + getCountryAndLanguagePathFromUrl();
const countryCodes = [
    {country: 'de', code: '49'},
    {country: 'at', code: '43'},
    {country: 'ch', code: '41'},
    {country: 'lu', code: '352'},
    {country: 'fr', code: '33'},
    {country: 'be', code: '32'},
    {country: 'nl', code: '31'}
]

const bookingInfo = computed(() => {
  let obj = JSON.parse(window.location_finder_studios);

  if (typeof obj.booking === 'object' && !!obj.booking.uid) {
    return obj.booking;
  } else {
    return false;
  }
});

const actionHelptext = computed(() => {
  if(bookingInfo.value && bookingInfo.value.checkbox_et_text.length) {
    return bookingInfo.value.checkbox_et_text
  } else {
    return t('et.action.helptext');
  }
});
const biaHelptext = computed(() => {
  if(bookingInfo.value && bookingInfo.value.checkbox_bia_text.length) {
    return bookingInfo.value.checkbox_bia_text
  } else {
    return t('et.bia.helptext');
  }
});

const data = reactive({
  locationfinder: '',
  action: true,
  bia: false,
  salutation: '',
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  day: '',
  time: '',
  data: false,
  privacy: false,
  newsletter: false
});

const selectedStudio = computed(() => {
  if(data.locationfinder && Object.keys(getStudioByName(data.locationfinder)).length > 0) {
    return getStudioByName(data.locationfinder);
  }
});

onBeforeMount(() => {
  countryCodes.forEach((cc) => {
    if (cc.country === getCountryFromUrl()) {
      data.phone = '+' + cc.code;
    }
  });
});

/**
 * Date/Time
 */
const selectedStudioDates = ref([{},{},{}]);
const selectedStudioDateTimes = ref([]);

watch(() => data.locationfinder, (newValue) => {
  let studio = getStudioByName(newValue);

  if(Object.keys(studio).length > 0) {
    trackData({
      'event': 'switch_studio'
    });

    selectedStudioDates.value = studio.cbc;
  } else {
    selectedStudioDates.value = [{},{},{}];
  }
});

watch(() => data.firstname, (newValue) => {
  if(isValidFirstName.value && isValidLastName.value) {
    trackData({
      'event': 'firstname_lastname_filled'
    });
  }
});
watch(() => data.lastname, (newValue) => {
  if(isValidFirstName.value && isValidLastName.value) {
    trackData({
      'event': 'firstname_lastname_filled'
    });
  }
});

watch(() => data.day, (newValue) => {
  let studio = getStudioByName(data.locationfinder),
      formattedOptions = [];

  data.time = '';

  if(newValue && (Object.keys(studio).length > 0) && Object.keys(selectedStudioDates.value[0]).length > 0) {

    studio?.cbc.forEach((date) => {
      if(date.day === newValue) {
        formattedOptions = [];

        // format array to match select field requirements
        date.ranges.forEach((range) => {
          formattedOptions.push({
            text: range,
            value: range
          })
        });

        selectedStudioDateTimes.value = formattedOptions;
      }
    });
  } else {
    selectedStudioDateTimes.value = [];
  }
});

/**
 * Form Validation
 */
const isValidStudio = computed(() => {
  return data.locationfinder.length;
});
const isValidInterest = computed(() => {
  return data.action || data.bia;
});
const isValidSalutation = ref(false);
const isValidFirstName = ref(false);
const isValidLastName = ref(false);
const isValidEmail = ref(false);
const isValidPhone = ref(false);
const isValidDate = ref(false);
const isValidTime = ref(false);
const isValidData = ref(false);
const isValidPrivacy = ref(false);

const isFormSend = ref(false);
const formSendError = ref(false);
const isSubmitLoading = ref(false);

const forceValidationOnSubmit = ref(false);

const studioRules = [
    (value) => value.length > 0 || t('et.studio.error.empty'),
    (value) => Object.keys(getStudioByName(value)).length > 0 || t('et.studio.error.studio'),
];
const salutationRules = [(value) => value.length > 0 || t('et.salutation.error.empty') ];
const firstnameRules = [(value) => value.length > 0 || t('et.firstname.error.empty') ];
const lastnameRules = [(value) => value.length > 0 || t('et.lastname.error.empty') ];
const emailRules = [
  (value) => value.length > 0 || t('et.email.error.empty'),
  (value) => /.+@.+\..+/.test(value) || t('et.email.error.email'),
];
const phoneRules = [
    (value) => countryCodes.some((item) => { return value.includes(item.code, 1); }) || t('et.phone.error.cc'),
    (value) => countryCodes.some((item) => { return value.includes(item.code, 1) && (value.length - 1) > item.code.length; }) || t('et.phone.error.empty'),
    (value) => /^\+[0-9\s]*$/.test(value) || t('et.phone.error.digits'),
];
const dateRules = [
    (value) => Object.keys(selectedStudioDates.value[0]).length > 0 || t('et.date.error.studio'),
    (value) => value.length > 0 || t('et.date.error.empty')
];
const timeRules = [
    (value) => data.day.length > 0 || t('et.time.error.date'),
    (value) => value.length > 0 || t('et.time.error.empty'),
];
const dataRules = [(value) => value === true || t('et.data.error.empty') ];
const privacyRules = [(value) => value === true || t('et.privacy.error.empty') ];

const handleStudioValidation = (isValid) => {isValidStudio.value = isValid;};
const handleSalutationValidation = (isValid) => {isValidSalutation.value = isValid;};
const handleFirstNameValidation = (isValid) => {isValidFirstName.value = isValid;};
const handleLastNameValidation = (isValid) => {isValidLastName.value = isValid;};
const handleEmailValidation = (isValid) => {isValidEmail.value = isValid;};
const handlePhoneValidation = (isValid) => {isValidPhone.value = isValid;};
const handleDateValidation = (isValid) => {isValidDate.value = isValid;};
const handleTimeValidation = (isValid) => {isValidTime.value = isValid;};
const handleDataValidation = (isValid) => {isValidData.value = isValid;};
const handlePrivacyValidation = (isValid) => {isValidPrivacy.value = isValid;};

const isFormValid = computed(() => isValidStudio.value && isValidInterest.value && isValidSalutation.value && isValidFirstName.value && isValidLastName.value && isValidEmail.value && isValidPhone.value && isValidDate.value && isValidTime.value && isValidData.value && isValidPrivacy.value);

const handleSubmit = () => {
  forceValidationOnSubmit.value = true;
  isSubmitLoading.value = true;

  setTimeout(() => {
    if (isFormValid.value) {
      let formData = {},
          emailSha256 = '',
          phoneSha256 = '';

      formData['salutation'] = data.salutation;
      formData['firstname'] = data.firstname;
      formData['lastname'] = data.lastname;
      formData['email'] = data.email;
      formData['phone'] = data.phone.replace('+', '00');

      emailSha256 = normalizeEmailForUserHash(formData['email']);
      emailSha256 = hashSha256(emailSha256);
      phoneSha256 = hashSha256(formData['phone'])

      formData['action'] = data.action;
      formData['bia'] = data.bia;

      formData['studio_uid'] = getStudioUidByName(data.locationfinder);
      formData['booking_uid'] = bookingInfo.value ? bookingInfo.value.uid : '';

      formData['browser'] = getBrowser();
      formData['href'] = window.location.href;

      formData['callback_date'] = JSON.stringify(getFormattedCallbackDate());
      formData['kies_ad_ref'] = JSON.stringify(decodeURIComponent(getCookie('kies_ad_ref'))) ?? {};
      formData['kies_ad_et'] = JSON.stringify(getKiesAdEtVal()) ?? {};

      axios.post(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/api/v1/conversion/', formData).then(response => {
        console.info(response.status);

        isFormSend.value = true;
        formSendError.value = false;

        trackData({
          'event': 'generate_lead',
          'form_name': 'Probetraining',
          'lead_trial': data.action,
          'lead_bia': data.bia,
          'studio_id': formData.studio_uid,
          'studio_name': data.locationfinder,
          'booking_id': formData.booking_uid,
          'first_party_data': {
            'sha256_email_address': emailSha256,
            'sha256_phone_number': phoneSha256
          }
        });

        // go to success page on submit
        switch (getLanguageFromUrl()) {
          case 'de':
            window.location.replace(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/probetraining/erfolgreich/?studio=' + formData.studio_uid);
            break;
          case 'en':
            window.location.replace(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/book-now/successful/?studio=' + formData.studio_uid);
            break;
          case 'fr':
            window.location.replace(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/essayez-le-maintenant/reussir/?studio=' + formData.studio_uid);
            break;
        }
      }).catch(error => {
        console.error(error.status);

        // server error
        isFormSend.value = false;
        formSendError.value = true;

        trackData({
          'event': 'generate_lead_error',
          'error': 'server_error',
          'form_name': 'Probetraining'
        });
      });

    } else {
      // form error
      isSubmitLoading.value = false;

      trackData({
        'event': 'generate_lead_error',
        'error': 'form_error',
        'form_name': 'Probetraining'
      });
    }
  }, 0);
};

const getFormattedCallbackDate = () => {
  let time = data.time,
      timestamp = selectedStudioDates.value.filter((date) => date.day === data.day)[0]?.ts,
      date = new Date(timestamp * 1000);

  time = time.split('-');

  return {
    date: data.day + date.getFullYear(),
    time_from: time[0],
    time_to: time[1]
  }
}

const getKiesAdEtVal = () => {
  let adsCookie = initialAdsCookie;

  if(checkCookies()) {
    if(!!getCookie('kies_ad_et')) {
      adsCookie = JSON.parse(decodeURIComponent(getCookie('kies_ad_et')));
    }

    adsCookie.consent = window.uc_kies_ad_et ? 'accepted' : 'declined';
  } else {
    adsCookie.consent = 'browser rejects cookies';
  }

  return adsCookie;
}

</script>